/**
 * Created by Ning Jiang on 2022-06-10.
 */

import {ReelStopPresentationController} from "../ReelStopPresentationController";
import {IReelStateEventData, ReelState} from "../../../core/reel/ReelState";
import {Serial} from "../../Serial";
import {SerialSound} from "../../sound/SerialSound";
import {Layers, SerialLayers} from "../../view/SerialLayers";
import {ReelPositionController} from "../../reels/ReelPositionController";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";

export class GlitchContainer extends PIXI.Container {

    private _glitchTop:TimelineSprite;
    private _glitchBottom:TimelineSprite;
    private _glitchLeft:TimelineSprite;
    private _glitchRight:TimelineSprite;

    constructor() {
        super();
        this._glitchTop = this.createNewGlitch();
        this._glitchBottom = this.createNewGlitch();
        this._glitchLeft = this.createNewGlitch();
        this._glitchLeft.rotation = Math.PI * 0.5;
        this._glitchRight = this.createNewGlitch();
        this._glitchRight.rotation = Math.PI * 0.5;
    }

    private createNewGlitch():TimelineSprite {
        const glitch = new TimelineSprite(Serial.assetLoader.getTextures("common/glitch/g"));
        glitch.blendMode = PIXI.BLEND_MODES.ADD;
        glitch.anchor.set(0.5, 0.5);
        glitch.alpha = 0.5;
        glitch.scale.set(335, 0.2);
        glitch.position.set(360, 360);
        this.addChild(glitch);
        return glitch;
    }

    public play():void {
        SerialLayers.getLayer(Layers.NEAR_WIN)!.addChild(this);
        this._glitchTop.playLoop();
        this._glitchBottom.playLoop();
        this._glitchLeft.playLoop();
        this._glitchRight.playLoop();
        this.resize();
    }

    public onOrientationChanged() {
        this.resize();
    }

    public onResize() {
        this.resize();
    }

    private resize():void {
        this._glitchTop.y = NolimitApplication.screenBounds.top + 5;
        this._glitchBottom.y = NolimitApplication.screenBounds.bottom - 5;
        this._glitchLeft.x = NolimitApplication.screenBounds.left + 5;
        this._glitchRight.x = NolimitApplication.screenBounds.right - 5;
    }

    public stop():void {
        this._glitchTop.stopLoop();
        this._glitchBottom.stopLoop();
        this._glitchLeft.stopLoop();
        this._glitchRight.stopLoop();
        if(this.parent) {
            this.parent.removeChild(this);
        }
    }
}

export class ReelNearWinController extends ReelStopPresentationController {

    private _defaultNearWinSpinStepDuration:number = 0.02;
    private _nearWinReel:number = -1;
    private _soundIndex:number = -1;

    private _backSpine:PIXI.spine.Spine;
    private _foreSpine:PIXI.spine.Spine;
    private _glitch:GlitchContainer;

    constructor() {
        super("ReelNearWinController");
    }

    protected onReelBounce(data:IReelStateEventData):Promise<any> {
        const reel = Serial.reels[data.reelId];
        if(reel.isQuickStop) {
            if(this._nearWinReel > -1) {
                this.stopNearWin();
            }
            return Promise.resolve();
        }

        // Find the next spinning reel.
        const nearWinId = this.findNextNearWinReel(data.reelId);
        if(nearWinId < 0) {
            if(this._nearWinReel > -1) {
                this.stopNearWin();
            }
            return Promise.resolve();
        }

        if(this._nearWinReel > -1 && !Serial.reelStopSoundController.stopSoundPlayed[data.reelId][0]) {
            Serial.reelStopSoundController.stopSoundPlayed[data.reelId][0] = true;
            SerialSound.soundPlayer.playTrack({name : SerialSound.ANTICIPATION_REEL_STOP});
        }
        // Logger.logDev(`start near win in reel ${nearWinId}`);
        this.setReelNearWinSpin(nearWinId);
        this.playNearWin(nearWinId);
        return Promise.resolve();
    }

    private findNextNearWinReel(reelId:number):number {
        do {
            reelId++;
        } while(reelId < 5 && Serial.reels[reelId].reelState !== ReelState.SPIN && Serial.reels[reelId].reelState !== ReelState.STOPPING);

        if(reelId < 5 && Serial.model.parsedGameData.nearWinReels[reelId]) {
            return reelId;
        } else {
            return -1;
        }
    }

    private setReelNearWinSpin(reelId:number):void {
        if(Serial.reels[reelId].reelView.nextSpinStepConfig) {
            Serial.reels[reelId].reelView.nextSpinStepConfig!.duration = this._defaultNearWinSpinStepDuration;
        } else {
            Serial.reels[reelId].reelView.nextSpinStepConfig = {
                duration : this._defaultNearWinSpinStepDuration,
            }
        }
    }

    private getNearWinSound():string {
        this._soundIndex++;
        if(this._soundIndex >= SerialSound.ANTICIPATION.length) {
            this._soundIndex = 0;
        }
        return SerialSound.ANTICIPATION[this._soundIndex];
    }

    private playNearWin(reelId:number):void {
        if(this._nearWinReel < 0) {
            SerialSound.fadeMusic(0.3, 1);
            SerialSound.soundPlayer.playTrack({name : this.getNearWinSound()});
            this.playGlitch();
        }
        this._nearWinReel = reelId;
        if(!this._backSpine) {
            this._backSpine = new PIXI.spine.Spine(Serial.assetLoader.getSpineAsset(`nearWinBack`));
            this._backSpine.autoUpdate = true;
            this._backSpine.position.set(360, 360);
        }
        if(!this._foreSpine) {
            this._foreSpine = new PIXI.spine.Spine(Serial.assetLoader.getSpineAsset(`nearWinFore`));
            this._foreSpine.autoUpdate = true;

        }
        SerialLayers.getLayer(Layers.NEAR_WIN)!.addChildAt(this._backSpine, 0);
        this._backSpine.state.setAnimation(0, "loop", true);
        this._foreSpine.position.set(ReelPositionController.REEL_AREAS[this._nearWinReel].x, 360);
        SerialLayers.getLayer(Layers.NEAR_WIN)!.addChildAt(this._foreSpine, 1);
        this._foreSpine.state.setAnimation(0, "animation", true);
    }

    private stopNearWin():void {
        this.stopGlitch();
        if(this._backSpine) {
            this._backSpine.state.clearTrack(0);
            this._backSpine.skeleton.setToSetupPose();
            this._backSpine.state.setEmptyAnimation(0, 0);
            if(this._backSpine.parent) {
                this._backSpine.parent.removeChild(this._backSpine);
            }
        }
        if(this._foreSpine) {
            this._foreSpine.state.clearTrack(0);
            this._foreSpine.skeleton.setToSetupPose();
            this._foreSpine.state.setEmptyAnimation(0, 0);
            if(this._foreSpine.parent) {
                this._foreSpine.parent.removeChild(this._foreSpine);
            }
        }
        SerialSound.fadeMusic(1, 1);
        SerialSound.soundPlayer.stopTrack(SerialSound.ANTICIPATION[0]);
        SerialSound.soundPlayer.stopTrack(SerialSound.ANTICIPATION[1]);
        if(this._nearWinReel === 4) {
            if(Serial.model.parsedGameData.scatterPositions[4] === -1) {
                SerialSound.soundPlayer.playTrack({name : SerialSound.ANTICIPATION_FAIL});
            }
        } else {
            if(Serial.model.parsedGameData.scatterPositions[1] === -1 ||
                Serial.model.parsedGameData.scatterPositions[2] === -1 ||
                Serial.model.parsedGameData.scatterPositions[3] === -1) {
                SerialSound.soundPlayer.playTrack({name : SerialSound.ANTICIPATION_FAIL});
            }
        }
        this._nearWinReel = -1;
    }

    private playGlitch():void {
        if(!this._glitch) {
            this._glitch = new GlitchContainer();
        }
        this._glitch.play();
    }

    private stopGlitch():void {
        if(!this._glitch) {
            return;
        }
        this._glitch.stop();
    }
}